/* eslint-disable react/no-array-index-key */
/* eslint-disable consistent-return */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
import { useEffect, useState } from "react";
import { produce } from "immer";

import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import axios from "axios";
import { findLast, set, sortBy, trim } from "lodash";
import api from "@shared/phoneDncStatusApi";
import { snakefyKeys, tryCatchHandlr } from "@shared/Utilities";
import { getPostJsonHeaders } from "@shared/axiosHelpers";

import { getExternalApi } from "@shared/externalApi";
import TextButton from "@shared/v2/TextButton";
import { PopoverTrigger, Popover, PopoverContent, PopoverItem } from "@shared/v2/Popover";
import AddressCard from "@shared/AddressCard";
import IconButton from "@shared/v2/IconButton";
import { EllipsisSolidV6, PencilSolidV6, PlusSolidV6, TrashCanSolidV6 } from "@shared/v2/Icomoon";
// import Banner from "@shared/v2/Banner";
import { useAddressModal, useAddressModalCallback } from "@shared/AddressModal";
import { getAddressAttributes } from "@shared/v2/HandwrittenCard";
import { useStandardizedAddresses } from "@shared/v2/hooks";

import * as css from "./person-detail-styles.module.css";
import EditableElementWithAdd from "./EditableElementWithAdd";
import SocialIcon from "./SocialIcon";
import EditSocialMediaModal from "./modals/EditSocialMediaModal";
import Loading from "../../Goals/components/Loader";
import {
  closeEditSocialMediaModal,
  openEditSocialMediaModal,
  storeSellScores,
  updatePersonData,
} from "../actions/personDetailActionCreators";
import CollapsibleElement from "./CollapsibleElement";
import DuplicateLeadCard from "./DuplicateLeadCard";
import { useMessagingServiceUrl } from "../../reducers/messagingReducer/selectors";
import useIsEditable from "../shared/useIsEditable";
import AreaPro from "./AreaPro";
import { useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import DeleteAddressModal from "./DeleteAddressModal";
import { updatePersonPromise } from "../actions/updatePerson";
import { getSuggestedAddressCheck } from "../shared/api";

const STYLES = {
  ellipsisButton: "tw-w-[178px] tw-flex tw-items-center tw-gap-[8px]",
  ellipsisTxt: "tw-text-14d tw-text-gray-75",
};
const suggestAddressCb = (payload) => getSuggestedAddressCheck(payload);

const submitAddressCallBack =
  ({ person, dispatch, payload }) =>
    async () => {
      const editablePerson = produce(person, (draft) => {
        draft.included.forEach((item) => {
          if (item.type === "address" && item.attributes.address_type === payload.addressType) {
            item.attributes.address_type = "other";
          }
        });

        draft.included.push({
          type: "address",
          attributes: {
            address_type: payload.addressType,
            street_address: payload.streetAddress,
            secondary_address: payload.secondaryAddress,
            city: payload.city,
            locality: payload.locality,
            postal_code: payload.postalCode,
            country: payload.country,
            tempId: Date.now(),
          },
        });
      });

      const [res, err] = await tryCatchHandlr(updatePersonPromise(editablePerson));

      if (err) {
        return {
          error:
            err.response?.data?.errors ||
            "There was an error submitting the address - double check your fields and try again, or exit out of the modal.",
        };
      }

      if (payload.standardized) {
        set(findLast(res.data.included, { type: "address" }), "attributes.standardized", "standardized");
      }

      dispatch(updatePersonData(res.data));
      return { error: null };
    };

const submitEditAddressCallBack = async ({ person, addressId, newAttrs, dispatch }) => {
  const editablePerson = produce(person, (draft) => {
    const addr = draft.included?.find((item) => item.type === "address" && item.id === addressId);
    if (!addr) return;

    const snakeNew = snakefyKeys(newAttrs || {});
    addr.attributes = {
      ...addr.attributes,
      ...snakeNew,
    };

    const finalType = addr.attributes?.address_type;
    if (!finalType) return;

    draft.included.forEach((item) => {
      if (item !== addr && item.type === "address" && item.attributes?.address_type === finalType) {
        item.attributes.address_type = "other";
      }
    });
  });

  const [res, err] = await tryCatchHandlr(updatePersonPromise(editablePerson));

  if (err) {
    return {
      error:
        err.response?.data?.errors ||
        "There was an error submitting the address — please check your fields and try again.",
    };
  }

  dispatch(
    updatePersonData(
      produce(res.data, (draft) => {
        const addr = draft.included.find((item) => item.type === "address" && item.id === addressId);

        if (addr) {
          addr.attributes = {
            ...addr.attributes,
            standardized: newAttrs.standardized ? "standardized" : "invalid",
          };
        }
      }),
    ),
  );
  return { error: null };
};

const getListingStatusColors = (status) => {
  switch (status) {
    case "Active":
      return { text: "tw-text-[#36b25f]", background: "tw-bg-[#EDFAF1]" };
    case "Pending":
    case "Active Under Contract":
      return { text: "tw-text-[#e5941b]", background: "tw-bg-[#faf1e6]" };
    case "Closed":
      return { text: "tw-text-[#e5342e]", background: "tw-bg-[#fae6e6]" };
    case "Withdrawn":
    default:
      return { text: "tw-text-[#666666]", background: "tw-bg-[#f5f7f7]" };
  }
};

const phoneNumbers = (person) =>
  (person.included || [])
    .filter((a) => a.type === "phone_detail" && a.id !== null)
    .map((phone) => phone.attributes?.value);

const getFormattedAddressesHomeFirst = (person) =>
  getAddressAttributes(person)
    .sort((a) => (a.address_type === "home" ? -1 : 1))
    .map((address) => ({
      streetAddress: address.street_address,
      secondaryAddress: address.secondary_address,
      city: address.city,
      state: address.locality,
      postalCode: address.postal_code,
      country: address.country,
    }));

const mapAddress = (addressAttributes, addressListingStatuses, standardized) => ({
  addressType: addressAttributes.address_type,
  streetAddress: addressAttributes.street_address,
  secondaryAddress: addressAttributes.secondary_address,
  country: addressAttributes.country,
  cityStateZip: `${addressAttributes.city}, ${addressAttributes.locality} ${addressAttributes.postal_code_formatted}`,
  status: addressListingStatuses[addressAttributes.id]?.status || "Off Market",
  preStatus: addressListingStatuses[addressAttributes.id]?.pre_status || "",
  updated: addressListingStatuses[addressAttributes.id]?.updated || "",
  id: addressAttributes.id,
  standardized: addressAttributes.standardized || standardized,
});


export const DEFAULT_INVALID_ADDRESS = {
  address: null,
  onEdit: null,
  onSubmit: null,
};
export const DEFAULT_VERIFY_ADDRESS = {
  originalAddress: null,
  suggestedAddress: null,
  onSubmit: null,
  onEdit: null,
};

export const AddressDetailsElement = ({
  disabled,
  addressListingStatuses,
  usedAddresses,
  openAddressModal,
  setDeleteCallBackModal,
  setDeleteAddressString,
}) => {
  const dispatch = useDispatch();
  const person = useSelector((state) => state.personDetail.person);
  const { standardizedAddressesHash } = useStandardizedAddresses(
    person,
    person?.data?.attributes?.slug,
  );

  // const featureFlag = useFeatureFlags();

  useEffect(() => {
    if (!Object.keys(standardizedAddressesHash).length) return;

    const sellScores = Object.entries(standardizedAddressesHash).reduce((acc, [addressId, details]) => {
      acc[addressId] = details?.sellScore;
      return acc;
    }, {});

    dispatch(storeSellScores(sellScores));
  }, [standardizedAddressesHash]);

  const addressDetails = getAddressAttributes(person).map((address) =>
    mapAddress(address, addressListingStatuses, standardizedAddressesHash[address.id]?.standardization)
  );

  // const hasSellScore = Object.values(standardizedAddressesHash).some((details) =>
  //   Boolean(details?.sellScore),
  // );

  return (
    <div className="tw-py-[8px] tw-group" data-cy="address_pdp_card">
      <div className="tw-flex tw-justify-between tw-items-center tw-px-[20px]">
        <div
          className={css.brivityPersonContactLabel}
        >{`Address${addressDetails.length > 1 ? "es" : ""}${addressDetails.length === 0 ? "" : ` (${addressDetails.length})`} `}</div>

        {!disabled && Boolean(addressDetails.length) && (
          <div className="tw-flex tw-gap-[4px] tw-invisible group-hover:tw-visible">
            <IconButton size="small" onClick={() => openAddressModal()}>
              <PlusSolidV6 />
            </IconButton>
          </div>
        )}
      </div>

      {/* Will implement once we have clear error states */}
      {/* {featureFlag.sellScore && (
        isSellScoreLoading ? (
          <div className="tw-relative tw-h-[40px] tw-mx-[20px] tw-mt-8px">
            <Loading />
          </div>
        ) : (
          <Banner
            show={!hasSellScore}
            containerClassName="!tw-border-[#fae4c3] !tw-bg-[#faf1e6] !tw-rounded-[4px] tw-py-[12px] tw-mx-[20px] tw-mt-8px tw-w-auto !tw-w-fit"
          >
            <div className="tw-flex tw-flex-col tw-gap-[4px] tw-text-[12px] tw-leading-[16px] tw-text-[#ad6714]">
              <span className="tw-font-semibold">Sell Score currently unavailable</span>
              <span>Stay tuned - we&apos;ll have things back up and running shortly.</span>
            </div>
          </Banner>
        )
      )} */}

      {addressDetails.length === 0 && (
        <TextButton
          schema="sentence"
          onClick={() => openAddressModal()}
          disabled={disabled}
          className="tw-px-20px"
        >
          Add Address
        </TextButton>
      )}
      {addressDetails.map((address, i) => (
        <AddressDetails
          key={i}
          address={address}
          usedAddresses={usedAddresses}
          setDeleteCallBackModal={setDeleteCallBackModal}
          setDeleteAddressString={setDeleteAddressString}
        />
      ))}
    </div>
  );
};

AddressDetailsElement.propTypes = {
  disabled: PropTypes.bool.isRequired,
  addressListingStatuses: PropTypes.objectOf(
    PropTypes.shape({
      status: PropTypes.string,
      pre_status: PropTypes.string,
      updated: PropTypes.string,
    }),
  ).isRequired,
  usedAddresses: PropTypes.arrayOf(
    PropTypes.shape({
      addressType: PropTypes.string,
      streetAddress: PropTypes.string,
      id: PropTypes.string,
    }),
  ).isRequired,
  openAddressModal: PropTypes.func.isRequired,
  setDeleteCallBackModal: PropTypes.func.isRequired,
  setDeleteAddressString: PropTypes.func.isRequired,
};

const ContactInfo = () => {
  const dispatch = useDispatch();
  const person = useSelector((state) => state.personDetail.person);
  const editSocialMediaModalOpen = useSelector((state) => state.personDetail.editSocialMediaModalOpen);
  const messagingServiceUrl = useMessagingServiceUrl();
  const messagingApi = getExternalApi(messagingServiceUrl);
  const [dncStatuses, setDncStatuses] = useState({});
  const [conversationCounts, setConversationCounts] = useState({});
  const [addressListingStatuses, setAddressListingStatuses] = useState({});
  const addressModal = useAddressModal("addEdit");
  const invalidAddressModal = useAddressModal("invalid");
  const verifyAddressModal = useAddressModal("verify");
  const {
    setOnSubmit,
    setInvalidOnSubmit,
    setInvalidOnEdit,
    setVerifyOnSubmit,
    setVerifyOnEdit,
  } = useAddressModalCallback();
  const [deleteCallBackModal, setDeleteCallBackModal] = useState(null);
  const [deleteAddressString, setDeleteAddressString] = useState("");

  const disabled = !useIsEditable();

  const featureFlag = useFeatureFlags();
  let addresses = [];
  if (featureFlag.areaproUser) {
    addresses = getFormattedAddressesHomeFirst(person);
    if (addresses.length > 4) {
      addresses = addresses.slice(0, 4);
    }
  }

  const getPhoneStatuses = () =>
    api
      .index([phoneNumbers(person)])
      .then(({ data }) => setDncStatuses(data))
      .catch(() => "dnc lookup failed");

  const getConversationCounts = () =>
    Promise.all(
      phoneNumbers(person).map((phoneNumber) =>
        messagingApi
          .get(
            `/Conversation/SmsAndGroupSmsCountForPersonAddressPair/${person.data?.attributes.uuid}/${phoneNumber}`,
          )
          .then((res) => ({ [phoneNumber]: res.data.total }))
          .catch(() => ({ [phoneNumber]: 0 })),
      ),
    )
      .then((res) => res.reduce((acc, val) => ({ ...acc, ...val }), {}))
      .then(setConversationCounts);

  useEffect(() => {
    getPhoneStatuses();
  }, [phoneNumbers(person).join("")]);

  useEffect(() => {
    if (!person?.data) {
      return;
    }

    const getAddresses = async () => {
      const [res] = await tryCatchHandlr(
        axios.get(`/api/v3/leads/${person.data.attributes.uuid}/addresses`, getPostJsonHeaders()),
      );
      setAddressListingStatuses(res?.data || {});
    };

    getAddresses();
    getConversationCounts();
  }, [person]);

  const detailsObjects = (type) =>
    person.included?.filter((a) => a.type === type && a.id !== null).map((a) => a.attributes) || [];

  const isDncPhone = (element) => {
    const phoneNumber = element.value;
    return api.isDnc(phoneNumber, dncStatuses);
  };

  const emailDetailsElement = () => {
    if (detailsObjects("email_detail")?.length > 0) {
      return sortBy(detailsObjects("email_detail"), (e) => e.position, ["desc"]).map((email, index) => (
        <EditableElementWithAdd
          key={`email${index}`}
          disabled={disabled}
          person={person}
          type="email"
          id={parseInt(email.id, 10)}
          updatePersonData={(data) => dispatch(updatePersonData(data))}
        />
      ));
    }

    return (
      <EditableElementWithAdd
        person={person}
        disabled={disabled}
        type="email"
        updatePersonData={(data) => dispatch(updatePersonData(data))}
      />
    );
  };

  const phoneDetailsElement = () => {
    if (detailsObjects("phone_detail").length > 0) {
      return detailsObjects("phone_detail").map((phone, index) => (
        <EditableElementWithAdd
          key={`phone${index}-${phone.value}`}
          disabled={disabled}
          person={person}
          type="phone"
          id={parseInt(phone.id, 10)}
          updatePersonData={(data) => dispatch(updatePersonData(data))}
          isDnc={isDncPhone(phone)}
          conversationCount={conversationCounts[phone.value]}
        />
      ));
    }
    return (
      <EditableElementWithAdd
        disabled={disabled}
        person={person}
        type="phone"
        updatePersonData={(data) => dispatch(updatePersonData(data))}
        isDnc={false}
      />
    );
  };

  const usedAddresses =
    person?.included
      ?.filter((item) => item.type === "address" && item.id)
      .map((item) => ({
        addressType: item.attributes.address_type,
        streetAddress: item.attributes.street_address,
        id: item.id,
      })) || [];

  const handleAddressSubmit = async (payload) => {
    const { res: resGeoCode, err: errGeoCode } = await suggestAddressCb(payload);

    if (errGeoCode) {
      return {
        error:
          errGeoCode.response?.data?.errors ||
          "There was an error checking the address - double check your fields and try again, or exit out of the modal.",
      };
    }

    if (!resGeoCode.data?.addressDetails?.standardized) {
      if (trim(resGeoCode.data?.addressDetails?.streetAddress) === "") {
        // Set callbacks for InvalidAddressModal
        setInvalidOnEdit(() => {
          setOnSubmit(handleAddressSubmit);
          addressModal.open({
            initAddress: { ...payload },
            usedAddresses,
          });
        });
        setInvalidOnSubmit(submitAddressCallBack({
          person,
          dispatch,
          payload,
        }));
        // Open the InvalidAddressModal
        invalidAddressModal.open({
          address: { ...payload },
        });
      } else {
        const suggestedAddress = {
          addressType: payload.addressType,
          streetAddress: resGeoCode.data?.addressDetails?.streetAddress,
          secondaryAddress: resGeoCode.data?.addressDetails?.secondaryAddress,
          city: resGeoCode.data?.addressDetails?.city,
          locality: resGeoCode.data?.addressDetails?.state,
          postalCode: resGeoCode.data?.addressDetails?.postalCode,
          country: resGeoCode.data?.addressDetails?.country,
          standardized: true,
        };
        // Set callbacks for VerifyAddressModal
        setVerifyOnSubmit(async (newPayLoad) => {
          await suggestAddressCb(newPayLoad);
          await submitAddressCallBack({
            person,
            dispatch,
            payload: newPayLoad,
          })();
        });
        setVerifyOnEdit(() => {
          setOnSubmit(handleAddressSubmit);
          addressModal.open({
            initAddress: { ...payload },
            usedAddresses,
          });
        });
        // Open the VerifyAddressModal
        verifyAddressModal.open({
          originalAddress: { ...payload },
          suggestedAddress,
        });
      }
      return;
    }

    await submitAddressCallBack({
      person,
      dispatch,
      payload: {
        ...payload,
        standardized: true,
      },
    })();
  };

  const openAddressModal = (config = {}) => {
    setOnSubmit(handleAddressSubmit);
    addressModal.open({ usedAddresses, ...config });
  };

  const brivityPartnersElement = (
    <div className="tw-px-[20px] tw-flex tw-justify-between tw-items-center">
      <div className={css.brivityPersonDetailsLabel}>Brivity Partners</div>
    </div>
  );

  const socialMediaDetailsElement = () => (
    <div className="tw-py-[8px] tw-px-[20px] tw-group hover:tw-bg-gray-5">
      <div className="tw-flex tw-gap-[8px] tw-justify-between tw-items-center">
        <div className={css.brivityPersonDetailsLabel}>Social Media</div>
        {!disabled && (
          <IconButton
            size="small"
            className="tw-invisible group-hover:tw-visible"
            onClick={() => dispatch(openEditSocialMediaModal())}
          >
            <PencilSolidV6 />
          </IconButton>
        )}
      </div>
      {person.data && (
        <div className="tw-flex tw-gap-[24px] tw-w-full tw-flex-wrap">
          <SocialIcon link={person.data.attributes.facebook.value} icon="fa fa-facebook" />
          <SocialIcon link={person.data.attributes.twitter.value} icon="fab fa6-x-twitter" />
          <SocialIcon link={person.data.attributes.linkedin.value} icon="fa fa-linkedin" />
          <SocialIcon link={person.data.attributes.instagram.value} icon="fa fa-instagram" />
          <SocialIcon link={person.data.attributes.youtube.value} icon="fab fa6-youtube" />
          <SocialIcon link={person.data.attributes.tiktok.value} icon="fab fa6-tiktok" />
          <SocialIcon link={person.data.attributes.pinterest.value} icon="fab fa6-pinterest" />
        </div>
      )}
      <EditSocialMediaModal
        show={editSocialMediaModalOpen}
        onClose={() => dispatch(closeEditSocialMediaModal())}
      />
    </div>
  );

  return (
    <CollapsibleElement title="Contact Info" id="pdp-contact-info">
      {!person.data && <Loading />}
      <DeleteAddressModal
        show={Boolean(deleteCallBackModal)}
        onClose={() => setDeleteCallBackModal(null)}
        onSubmit={deleteCallBackModal}
        address={deleteAddressString}
      />
      <DuplicateLeadCard />
      <div id="person-contact-info-card" className="tw-mx-[-20px]">
        {emailDetailsElement()}

        {phoneDetailsElement()}

        <AddressDetailsElement
          disabled={disabled}
          addressListingStatuses={addressListingStatuses}
          usedAddresses={usedAddresses}
          openAddressModal={openAddressModal}
          setDeleteCallBackModal={setDeleteCallBackModal}
          setDeleteAddressString={setDeleteAddressString}
        />

        {featureFlag.areaproUser && (
          <>
            {brivityPartnersElement}
            <AreaPro addresses={addresses} />
          </>
        )}

        {socialMediaDetailsElement()}
      </div>
    </CollapsibleElement>
  );
};

export default ContactInfo;

const AddressDetails = ({
  address,
  usedAddresses,
  setDeleteCallBackModal,
  setDeleteAddressString,
}) => {
  const statusColors = getListingStatusColors(address.status);
  const person = useSelector((state) => state.personDetail.person);
  const sellScores = useSelector((state) => state.personDetail.sellScores);
  const dispatch = useDispatch();
  const addressModal = useAddressModal("addEdit");
  const invalidAddressModal = useAddressModal("invalid");
  const verifyAddressModal = useAddressModal("verify");
  const {
    setOnSubmit,
    setInvalidOnSubmit,
    setInvalidOnEdit,
    setVerifyOnSubmit,
    setVerifyOnEdit,
  } = useAddressModalCallback();

  const deleteCallBackModalHandler = () => {
    setDeleteAddressString(`${address.streetAddress}, ${address.cityStateZip}`);

    return setDeleteCallBackModal(() => async () => {
      const editablePerson = produce(person, (draft) => {
        const addressToUpdate = draft.included.find(
          (item) => item.type === "address" && item.id === address.id,
        );

        if (addressToUpdate) {
          addressToUpdate.attributes._destroy = true;
        }
      });
      const [res, err] = await tryCatchHandlr(updatePersonPromise(editablePerson));

      if (err) {
        return {
          error: err.response?.data?.errors || "There was an error deleting the address - please try again.",
        };
      }

      dispatch(updatePersonData(res.data));
      return { error: null };
    });
  };

  const createEditAddressSubmit = () => async (ad) => {
    const { res: resGeoCode, err: errGeoCode } = await suggestAddressCb(ad);

    if (errGeoCode) {
      return {
        error:
          errGeoCode.response?.data?.errors ||
          "There was an error checking the address - double check your fields and try again, or exit out of the modal.",
      };
    }
    const details = resGeoCode.data?.addressDetails;

    if (!details?.standardized) {
      addressModal.close();

      if (trim(details.streetAddress) === "") {
        setInvalidOnSubmit(() =>
          submitEditAddressCallBack({
            person,
            addressId: address.id,
            newAttrs: ad,
            dispatch,
          }),
        );
        setInvalidOnEdit(() => {
          setOnSubmit(createEditAddressSubmit());
          addressModal.open({
            initAddress: { ...ad },
            usedAddresses,
          });
          invalidAddressModal.close();
        });
        invalidAddressModal.open({
          address: { ...ad },
        });
      } else {
        setVerifyOnSubmit(async (newAd) => {
          await suggestAddressCb(newAd);
          await submitEditAddressCallBack({
            person,
            addressId: address.id,
            newAttrs: newAd,
            dispatch,
          });
        });
        setVerifyOnEdit(() => {
          setOnSubmit(createEditAddressSubmit());
          addressModal.open({
            initAddress: { ...ad },
            usedAddresses,
          });
          verifyAddressModal.close();
        });
        verifyAddressModal.open({
          originalAddress: { ...ad },
          suggestedAddress: {
            addressType: ad.addressType,
            streetAddress: details.streetAddress,
            secondaryAddress: details.secondaryAddress,
            city: details.city,
            locality: details.state,
            postalCode: details.postalCode,
            country: details.country,
            standardized: true,
          },
        });
      }
      return;
    }

    await submitEditAddressCallBack({
      person,
      addressId: address.id,
      newAttrs: { ...ad, standardized: true },
      dispatch,
    });
  };

  const handleEditClick = () => {
    const [cityPart, stateZip] = address.cityStateZip.split(",").map((str) => str.trim());
    const [locality, ...postalParts] = stateZip.split(" ");
    const postalCode = postalParts.join(" ");
    const city = cityPart;

    const initAddr = {
      addressType: address.addressType,
      streetAddress: address.streetAddress,
      secondaryAddress: address.secondaryAddress,
      country: address.country,
      standardized: address.standardized,
      id: address.id,
      city,
      locality,
      postalCode,
    };

    setOnSubmit(createEditAddressSubmit());
    addressModal.open({
      initAddress: initAddr,
      usedAddresses,
      title: "Edit Address",
    });
  };

  return (
    <div
      key={`ad-${address.streetAddress}`}
      className="tw-py-[8px] tw-px-[20px] tw-rounded-[8px] hover:tw-bg-gray-5 tw-flex tw-relative"
      data-cy="pdp_ind_addy_card"
    >
      <AddressCard
        containerClassName="tw-flex-1"
        addressType={address.addressType}
        addressStatus={address.status}
        preStatus={address.preStatus}
        addressStatusClassName={`${statusColors.background} ${statusColors.text}`}
        streetAddress={address.streetAddress}
        secondaryAddress={address.secondaryAddress}
        cityStateZip={address.cityStateZip}
        standardized={address.standardized}
        country={address.country}
        updatedDate={address.updated}
        sellScore={sellScores[address.id]}
      />
      <div className="tw-pt-8px tw-absolute tw-z-1 tw-right-20px">
        <Popover placement="bottom-end">
          <PopoverTrigger>
            <IconButton schema="transparent" size="small">
              <EllipsisSolidV6 size="m" className="tw-text-gray-75" />
            </IconButton>
          </PopoverTrigger>
          <PopoverContent>
            <PopoverItem onClick={handleEditClick} icon={<PencilSolidV6 />}>
              <span className={STYLES.ellipsisTxt}>Edit Address</span>
            </PopoverItem>
            <PopoverItem
              onClick={() => {
                deleteCallBackModalHandler();
              }}
              icon={<TrashCanSolidV6 />}
            >
              <span className={STYLES.ellipsisTxt}>Delete Address</span>
            </PopoverItem>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

AddressDetails.propTypes = {
  address: PropTypes.shape({
    addressType: PropTypes.string,
    streetAddress: PropTypes.string,
    secondaryAddress: PropTypes.string,
    cityStateZip: PropTypes.string,
    country: PropTypes.string,
    status: PropTypes.string,
    preStatus: PropTypes.string,
    updated: PropTypes.string,
    id: PropTypes.string,
    standardized: PropTypes.bool,
  }).isRequired,
  usedAddresses: PropTypes.arrayOf(
    PropTypes.shape({
      addressType: PropTypes.string,
      streetAddress: PropTypes.string,
      id: PropTypes.string,
    }),
  ).isRequired,
  setDeleteCallBackModal: PropTypes.func.isRequired,
  setDeleteAddressString: PropTypes.func.isRequired,
};
